
import { defineComponent, nextTick } from 'vue';
import { swalConfirm } from '@/core/helpers/utils';

/**
 * Confirmation adapter that shows SweetAlert when `isDisplay` becomes true.
 * Keeps legacy emits: on-close-button-click / on-close-icon-click / on-confirm-button-click.
 * Message comes from the default slot text (or the optional `message` prop).
 */
export default defineComponent({
  name: 'confirmation-dialog',

  emits: [
    'on-close-button-click',
    'on-confirm-button-click',
    'on-close-icon-click',
  ],

  props: {
    title: {
      type: String,
      required: true,
    },
    message: {
      type: String,
      default: '',
    },
    cancelText: {
      type: String,
      default: 'Hủy bỏ',
    },
    confirmText: {
      type: String,
      default: 'Xác nhận',
    },
    isDisplay: {
      type: Boolean,
      required: true,
    },
    // Kept for call-site compatibility; unused with SweetAlert
    width: {
      type: String,
      default: '50%',
    },
    size: {
      type: String,
      default: 'sm',
      validator: (value: string) => ['', 'sm', 'lg', 'xl'].includes(value),
    },
  },

  data() {
    return {
      isPromptOpen: false,
    };
  },

  watch: {
    isDisplay: {
      immediate: true,
      async handler(visible: boolean) {
        if (!visible || this.isPromptOpen) {
          return;
        }

        this.isPromptOpen = true;

        try {
          await nextTick();

          const slotText =
            (this.$refs.slotRoot as HTMLElement | undefined)?.textContent?.trim() ||
            '';
          const text = this.message || slotText;

          const confirmed = await swalConfirm({
            title: this.title,
            text,
            confirmButtonText: this.confirmText,
            cancelButtonText: this.cancelText,
          });

          if (confirmed) {
            this.$emit('on-confirm-button-click');
          } else {
            this.$emit('on-close-button-click');
            this.$emit('on-close-icon-click');
          }
        } finally {
          this.isPromptOpen = false;
        }
      },
    },
  },
});
